import { type FC, useEffect, useRef } from "react";
import { Button, Slide } from "@mui/material";
import { useMutation } from "@apollo/client";
import TrackWidgetMutation from "./TrackWidgetMutation.graphql";
import type { TrackWidgetMutationType } from "@/types/graphql/site.generated";

interface PromoButtonWidgetProps {
  // Контент, для которого сейчас показан виджет. VideoScreen/StreamScreen
  // намеренно не размонтируют <ContentPlayer> при смене контента (автопереход,
  // клик по похожему видео) — плеер и этот компонент переиспользуются с новыми
  // пропами. Content.widget часто резолвится в общий дефолтный виджет
  // организации, поэтому widget.id один и тот же у разных контентов: без
  // contentId в ключе гварда HIT для второго контента был бы молча пропущен.
  contentId?: string;
  widget?: {
    id: string;
    buttonUrl: string;
    buttonText: string;
    buttonColor: string;
    buttonTextColor: string;
  } | null;
  /** Видимость привязана к контролам плеера (референсный PromoButton.js —
   * Slide вместе с шапкой плеера), а не показывается постоянно: иначе кнопка
   * перекрывается TOP_RIGHT-аннотациями видео (у них zIndex выше). */
  visible?: boolean;
}

// Кнопка-CTA поверх плеера (порт референсного player/components/PromoButton).
// Рендерит null без виджета — тот же "безопасно передавать безусловно"
// конвенция, что и у CloseRouteDialogButton в этом же overlay-слоте.
export const PromoButtonWidget: FC<PromoButtonWidgetProps> = ({
  contentId,
  widget,
  visible = true,
}) => {
  const [trackWidget] =
    useMutation<TrackWidgetMutationType>(TrackWidgetMutation);

  // Ключ — пара contentId+widgetId, а не только widgetId: одинаковый
  // (дефолтный) виджет на разных контентах не должен глушить HIT при смене
  // просматриваемого контента без remount'а плеера.
  const trackedHitForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!widget) return;
    const hitKey = `${contentId}:${widget.id}`;
    if (trackedHitForRef.current === hitKey) return;
    trackedHitForRef.current = hitKey;
    void trackWidget({ variables: { widgetId: widget.id, event: "HIT" } });
  }, [contentId, widget, trackWidget]);

  if (!widget) return null;

  return (
    <Slide direction="down" in={visible} appear={false}>
      <Button
        disableElevation
        variant="contained"
        href={widget.buttonUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          void trackWidget({
            variables: { widgetId: widget.id, event: "CLICK" },
          });
        }}
        sx={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 10,
          minWidth: "auto",
          backgroundColor: widget.buttonColor,
          color: widget.buttonTextColor,
          "&:hover": { backgroundColor: widget.buttonColor },
        }}
      >
        {widget.buttonText}
      </Button>
    </Slide>
  );
};
